import { ApiConfig, ChatResponse } from '../types/chat';
import { getDeviceInfo } from './deviceInfo';

import {
  detectEnvironment,
  getCurrentEnvironmentConfig,
  getStreamingEndpoint,
} from './featureFlags';

// Initialise environment config for Agent Squad
const environmentConfig = getCurrentEnvironmentConfig();

console.log(
  `🌍 Environment: ${detectEnvironment()} (${environmentConfig.name})`,
);
console.log(`🔗 API Endpoint: ${environmentConfig.apiEndpoint}`);

// Default configuration with environment auto-detection
const DEFAULT_API_CONFIG: ApiConfig = {
  endpoint: environmentConfig.apiEndpoint,
};

export class ChatApiClient {
  private config: ApiConfig;
  private abortController: AbortController | null = null;
  private currentEnvironment: string;

  constructor(config?: Partial<ApiConfig>) {
    // Only use config values that are not null/undefined
    const validConfig: Partial<ApiConfig> = {};
    if (config?.endpoint && config.endpoint !== null) {
      validConfig.endpoint = config.endpoint;
    }

    this.config = { ...DEFAULT_API_CONFIG, ...validConfig };
    this.currentEnvironment = detectEnvironment();

    console.log('🔧 ChatApiClient initialised');
    console.log('🔗 Endpoint:', this.config.endpoint);
    console.log('🌍 Environment:', this.currentEnvironment);
  }

  // Direct Agent Squad requests via Function URL

  getEndpoint(): string | null {
    return getStreamingEndpoint(); // Note: this function returns the Function URL endpoint
  }

  async sendMessage(request: {
    content: string;
    user_id: string;
    session_id: string;
  }): Promise<ChatResponse> {
    const endpoint = this.getEndpoint();
    if (!endpoint) {
      throw new Error('No endpoint configured');
    }

    try {
      console.log('🚀 Sending Agent Squad request:', {
        content: request.content.substring(0, 50) + '...',
      });

      const chatEndpoint: string = endpoint + '/chat';

      const agentSquadRequest = {
        query: request.content,
        user_id: request.user_id,
        session_id: request.session_id,
      };

      const { os, deviceType } = getDeviceInfo();

      const response = await fetch(chatEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-Device-OS': os,
          'X-Device-Type': deviceType,
        },
        body: JSON.stringify(agentSquadRequest),
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      console.log('✅ Agent Squad response received');

      // Convert Agent Squad response to ChatResponse format
      return {
        response: data.response || data.message || '',
        sessionId: data.session_id || request.session_id,
        agentId: data.agent_name || 'agent-squad',
        citations: data.citations || [],
        debug: data.debug_info || {},
        orchestrator: 'agent_squad_native',
        timestamp: data.timestamp || new Date().toISOString(),
        trace: data.traces || [],
        collaboration_log: data.collaboration_log || [],
      };
    } catch (error) {
      console.error('❌ Agent Squad request error:', error);
      throw error;
    }
  }

  cancelRequest(): void {
    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
    }
  }

  updateConfig(config: Partial<ApiConfig>): void {
    this.config = { ...this.config, ...config };
  }
}

export const generateSessionId = (): string => {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

export const formatTimestamp = (date: Date): string => {
  return new Intl.DateTimeFormat('en-AU', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    timeZone: 'Australia/Sydney',
  }).format(date);
};

export const validateMessage = (message: string): boolean => {
  return message.trim().length > 0 && message.length <= 4000;
};

export const sanitizeMessage = (message: string): string => {
  return message.trim().replace(/\s+/g, ' ');
};
